/*!
 * CBD HUT — Collection Insights (section-safe)
 * - No global IDs / no inline onclick
 * - Modal accessibility + scroll lock that won't fight other locks
 * - Autoplay interval cleaned up on section unload
 */

(function () {
  'use strict';

  var INIT_ATTR = 'data-cbh-ci-init';

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      var ctx = this;
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function parseJsonScript(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return '';
    try { return JSON.parse(el.textContent); } catch (e) { return ''; }
  }

  function toBool(val, fallback) {
    if (val === undefined || val === null || val === '') return fallback;
    return val === true || val === 'true' || val === '1';
  }

  function lockScroll() {
    var body = document.body;
    if (body.hasAttribute('data-cbh-scroll-locked')) return;

    body.setAttribute('data-cbh-scroll-locked', 'true');
    body.dataset.cbhPrevOverflow = body.style.overflow || '';
    body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    var body = document.body;
    if (!body.hasAttribute('data-cbh-scroll-locked')) return;

    body.style.overflow = body.dataset.cbhPrevOverflow || '';
    delete body.dataset.cbhPrevOverflow;
    body.removeAttribute('data-cbh-scroll-locked');
  }

  function init(root) {
    if (!root || root.hasAttribute(INIT_ATTR)) return;
    root.setAttribute(INIT_ATTR, '1');

    var descText = root.querySelector('[data-ci-desc]');
    var showMoreBtn = root.querySelector('[data-ci-open]');
    var heading = root.querySelector('.collection-insights__heading');
    var modal = root.querySelector('[data-ci-modal]');
    var modalClose = root.querySelector('[data-ci-close]');
    var modalBox = root.querySelector('[data-ci-modal-box]');

    var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    var inlineDesktop = toBool(root.dataset.ciInlineDesktop, true);
    var hideHeadingDesktop = root.classList.contains('collection-insights--hide-heading-desktop');
    var inlineBreakpoint = parseInt(root.dataset.ciInlineBreakpoint, 10);
    if (isNaN(inlineBreakpoint)) inlineBreakpoint = 1500;

    var carousel = root.querySelector('[data-ci-carousel]');
    var carouselWrap = root.querySelector('.collection-insights__carousel-wrapper');
    var prevBtn = root.querySelector('[data-ci-prev]');
    var nextBtn = root.querySelector('[data-ci-next]');

    var showArrows = toBool(root.dataset.ciShowArrows, true);
    var showArrowsMobile = toBool(root.dataset.ciShowArrowsMobile, true);

    var fullHtml = parseJsonScript(root, 'script[data-ci-full-html]');
    var firstPara = parseJsonScript(root, 'script[data-ci-first-para]');
    var modalContent = root.querySelector('[data-ci-modal-content]');
    if (modalContent && fullHtml) modalContent.innerHTML = fullHtml;

    var cleanup = {
      intervalId: null,
      resumeTimeoutId: null,
      removeKeydown: null,
      removeResize: null,
      removeObserver: null,
      removeCarouselScroll: null,
      removeCarouselInteract: null
    };
    root._cbhCI = cleanup;

    function hasOverflow() {
      return !!(carousel && carousel.scrollWidth > (carousel.clientWidth + 8));
    }

    // Narrow-mode helper: used for table stacking + arrow behaviour in scaled previews
    // (e.g. Theme Editor "mobile" view which may not trigger real CSS media queries)
    function updateNarrowMode() {
      try {
        var rect = root.getBoundingClientRect();
        var w = rect && rect.width ? rect.width : 0;
        root.classList.toggle('collection-insights--narrow', w > 0 && w <= 620);
      } catch (e) {
        // ignore
      }
    }

    function updateDescription() {
      updateNarrowMode();
      if (!descText) return;

      var desktopMode = inlineDesktop && window.innerWidth >= inlineBreakpoint;

      if (!fullHtml) {
        descText.className = 'collection-insights__text';
        descText.innerHTML = '';
        if (showMoreBtn) showMoreBtn.hidden = true;
        if (heading) heading.hidden = false;
        return;
      }

      if (desktopMode) {
        descText.className = 'collection-insights__text';
        descText.innerHTML = fullHtml;
        if (showMoreBtn) showMoreBtn.hidden = true;
        if (heading) heading.hidden = !!hideHeadingDesktop;
      } else {
        descText.className = 'collection-insights__text collection-insights__text--mobile';
        descText.innerHTML = firstPara || fullHtml;
        if (showMoreBtn) showMoreBtn.hidden = false;
        if (heading) heading.hidden = false;
      }

      updateArrowVisibility();
      updateArrowDisabled();
      startAutoplay();
    }

    var onResize = debounce(updateDescription, 150);
    window.addEventListener('resize', onResize);
    cleanup.removeResize = function () {
      window.removeEventListener('resize', onResize);
    };

    function getScrollStep() {
      if (!carousel) return 140;
      var first = carousel.querySelector('.collection-insights__brand');
      if (!first) return 140;
      var rect = first.getBoundingClientRect();
      return Math.max(120, Math.round(rect.width + 15));
    }

    function scrollCarousel(dir) {
      if (!carousel) return;
      carousel.scrollBy({ left: dir * getScrollStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
    }

    function nudgeAutoplay() {
      stopAutoplay();
      if (cleanup.resumeTimeoutId) window.clearTimeout(cleanup.resumeTimeoutId);
      cleanup.resumeTimeoutId = window.setTimeout(function () { startAutoplay(); }, 3000);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollCarousel(-1); nudgeAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollCarousel(1); nudgeAutoplay(); });

    function updateArrowVisibility() {
      var enabled = showArrows && hasOverflow();
      var isMobile = window.innerWidth <= 768 || root.classList.contains('collection-insights--narrow');
      if (isMobile && !showArrowsMobile) enabled = false;

      if (prevBtn) prevBtn.hidden = !enabled;
      if (nextBtn) nextBtn.hidden = !enabled;
    }

    function updateArrowDisabled() {
      if (!carousel || !prevBtn || !nextBtn || prevBtn.hidden || nextBtn.hidden) return;
      var max = carousel.scrollWidth - carousel.clientWidth;
      prevBtn.disabled = carousel.scrollLeft <= 2;
      nextBtn.disabled = carousel.scrollLeft >= (max - 2);
    }

    if (carousel) {
      var onCarouselScroll = debounce(updateArrowDisabled, 60);
      carousel.addEventListener('scroll', onCarouselScroll, { passive: true });
      cleanup.removeCarouselScroll = function () {
        carousel.removeEventListener('scroll', onCarouselScroll);
      };
    }

    var lastFocus = null;

    function getFocusable(container) {
      if (!container) return [];
      var nodes = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.prototype.slice.call(nodes).filter(function (el) {
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
      });
    }

    function openModal() {
      if (!modal) return;
      lastFocus = document.activeElement;

      modal.classList.add('collection-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      lockScroll();

      if (modalBox) modalBox.focus();
    }

    function closeModal() {
      if (!modal) return;

      modal.classList.remove('collection-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      unlockScroll();

      if (lastFocus && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
    }

    if (showMoreBtn) showMoreBtn.addEventListener('click', openModal);
    if (modalClose) modalClose.addEventListener('click', closeModal);

    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });
    }

    function onKeydown(e) {
      if (!modal || !modal.classList.contains('collection-modal--open')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        return;
      }

      if (e.key !== 'Tab') return;

      var focusables = getFocusable(modalBox);
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === modalBox) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeydown);
    cleanup.removeKeydown = function () {
      document.removeEventListener('keydown', onKeydown);
    };

    // Autoplay (smarter + respects reduced motion)
    var autoplaySpeed = 0;
    var autoplayEnabled = false;
    var isInView = true;
    var isInteracting = false;

    if (carousel && carousel.dataset.autoplay) {
      autoplaySpeed = parseInt(carousel.dataset.autoplay, 10);
      autoplayEnabled = !prefersReduced && !isNaN(autoplaySpeed) && autoplaySpeed >= 500;
    }

    function startAutoplay() {
      if (!autoplayEnabled) return;
      if (!carousel || cleanup.intervalId) return;
      if (!hasOverflow()) return;
      if (!isInView || isInteracting) return;

      cleanup.intervalId = window.setInterval(function () {
        if (!carousel) return;

        var nearEnd = (carousel.scrollLeft + carousel.clientWidth) >= (carousel.scrollWidth - 20);
        if (nearEnd) carousel.scrollTo({ left: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
        else scrollCarousel(1);
      }, autoplaySpeed);
    }

    function stopAutoplay() {
      if (cleanup.intervalId) {
        window.clearInterval(cleanup.intervalId);
        cleanup.intervalId = null;
      }
    }

    if (autoplayEnabled && carousel && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries && entries[0];
        isInView = !!(entry && entry.isIntersecting);
        if (isInView) startAutoplay();
        else stopAutoplay();
      }, { threshold: 0.2 });

      io.observe(carousel);
      cleanup.removeObserver = function () {
        io.disconnect();
      };
    }

    function setInteracting(flag) {
      isInteracting = flag;
      if (flag) stopAutoplay();
      else startAutoplay();
    }

    if (carouselWrap) {
      var onEnter = function () { setInteracting(true); };
      var onLeave = function () { setInteracting(false); };
      var onFocusIn = function () { setInteracting(true); };
      var onFocusOut = function () { setInteracting(false); };
      var onPointerDown = function () { setInteracting(true); };
      var onPointerUp = function () {
        if (cleanup.resumeTimeoutId) window.clearTimeout(cleanup.resumeTimeoutId);
        cleanup.resumeTimeoutId = window.setTimeout(function () { setInteracting(false); }, 1600);
      };

      carouselWrap.addEventListener('mouseenter', onEnter);
      carouselWrap.addEventListener('mouseleave', onLeave);
      carouselWrap.addEventListener('focusin', onFocusIn);
      carouselWrap.addEventListener('focusout', onFocusOut);
      carouselWrap.addEventListener('pointerdown', onPointerDown);
      carouselWrap.addEventListener('pointerup', onPointerUp);
      carouselWrap.addEventListener('pointercancel', onPointerUp);

      cleanup.removeCarouselInteract = function () {
        carouselWrap.removeEventListener('mouseenter', onEnter);
        carouselWrap.removeEventListener('mouseleave', onLeave);
        carouselWrap.removeEventListener('focusin', onFocusIn);
        carouselWrap.removeEventListener('focusout', onFocusOut);
        carouselWrap.removeEventListener('pointerdown', onPointerDown);
        carouselWrap.removeEventListener('pointerup', onPointerUp);
        carouselWrap.removeEventListener('pointercancel', onPointerUp);
      };
    }

    updateDescription();
    updateArrowVisibility();
    updateArrowDisabled();
    startAutoplay();
  }

  function initAll(context) {
    var scope = context && context.querySelectorAll ? context : document;
    var roots = scope.querySelectorAll('[data-cbh-collection-insights]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  // Cleanup when section removed in Theme Editor
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-cbh-collection-insights]') : null;
    if (!root || !root._cbhCI) return;

    if (root._cbhCI.intervalId) window.clearInterval(root._cbhCI.intervalId);
    if (root._cbhCI.resumeTimeoutId) window.clearTimeout(root._cbhCI.resumeTimeoutId);
    if (root._cbhCI.removeKeydown) root._cbhCI.removeKeydown();
    if (root._cbhCI.removeResize) root._cbhCI.removeResize();
    if (root._cbhCI.removeObserver) root._cbhCI.removeObserver();
    if (root._cbhCI.removeCarouselScroll) root._cbhCI.removeCarouselScroll();
    if (root._cbhCI.removeCarouselInteract) root._cbhCI.removeCarouselInteract();

    delete root._cbhCI;
  });
})();
